import { createAppStore } from '@store/createAppStore';
import {
  ILoadBoardViewModel,
  LoadboardFetchOptsT,
  LoadBoardState,
  SavedSearchCategory,
  SavedSearchT,
} from '@store/loadboard/types';
import { logError } from '@utils/errorLogging';
import { Part, produce } from 'solid-js/store';

import { fetchLoadBoardItems, fetchSavedSearches } from './services';

const [loadBoardStore, setLoadBoardStore] = createAppStore<LoadBoardState>(
  {
    items: [] as ILoadBoardViewModel[],
    savedSearches: [],
    filters: [],
    cache: undefined,
    isLoading: true,
    isError: false,
    errorMessage: null,
    updateLock: false,
    overlay: false,
    membersByGroupName: {},
    officeGroupIdsByMachingNames: {},
    filteredItems: [] as ILoadBoardViewModel[],
    onlyEmpty: false,
    myItems: false,
    tabStatus: null,
    groupIds: [],
  },
  'loadBoardStore',
);

export const saveLoadboardCache = (cache: SavedSearchT | undefined) => {
  setLoadBoardStore(
    produce((draft: LoadBoardState) => {
      draft.cache = cache;
    }),
  );
};

/**
 * Fetches the loadboard load items and the saved searches. It places them on
 * the loadboard store for easy access.
 */
export const fetchLoadBoardDetails = async (opts?: LoadboardFetchOptsT) => {
  try {
    let savedSearches: SavedSearchT[] = [];
    const savedSearchesFromSession = sessionStorage.getItem('savedSearches');

    if (Boolean(savedSearchesFromSession)) {
      savedSearches = JSON.parse(
        savedSearchesFromSession as string,
      ) as SavedSearchT[];
    } else {
      savedSearches = await fetchSavedSearches();
      sessionStorage.setItem('savedSearches', JSON.stringify(savedSearches));
    }

    const { filters, cache } = savedSearches.reduce(
      (prev, s) => {
        if (s.category === SavedSearchCategory.Cache) {
          prev.cache = s;
        } else if (
          s.category === SavedSearchCategory.Basic ||
          s.category === SavedSearchCategory.Advanced
        ) {
          prev.filters.push(s);
        }

        return prev;
      },
      { filters: [], cache: undefined } as {
        filters: SavedSearchT[];
        cache: SavedSearchT | undefined;
      },
    );

    if (opts?.restoreMyItems !== false && cache !== undefined) {
      try {
        const gridState = JSON.parse(cache.params) as { myItems?: boolean };
        setLoadBoardStore(
          'myItems',
          typeof gridState.myItems === 'boolean' ? gridState.myItems : false,
        );
      } catch {
        // ignore malformed cache
      }
    }

    const shouldPassFetchOpts =
      opts?.status !== undefined ||
      opts?.restrictToMyUserId !== undefined ||
      opts?.startDate !== undefined ||
      opts?.endDate !== undefined;
    const resp = await fetchLoadBoardItems(
      shouldPassFetchOpts ? opts : undefined,
    );
    const items = resp.items;

    if (!resp.isEnabled) {
      setLoadBoardStore(
        produce((draft: LoadBoardState) => {
          draft.isError = true;
          draft.errorMessage = resp.message;
          draft.isLoading = false;
        }),
      );
      return;
    }
    resp.items.map((item) => {
      item.stopsRatio = `${item.pickUps} : ${item.dropOffs}`;
    });

    setLoadBoardStore(
      produce((draft: LoadBoardState) => {
        draft.isError = false;
        draft.errorMessage = null;
        draft.isLoading = false;
        draft.items = [...items];
        draft.savedSearches = [...savedSearches];
        draft.filters = [...filters];
        draft.cache = cache !== undefined ? { ...cache } : undefined;
        draft.membersByGroupName = resp.membersByGroupName;
        draft.officeGroupIdsByMachingNames = resp.officeGroupIdsByMachingNames;
        draft.groupIds = resp.groupIds;
      }),
    );
  } catch (error) {
    logError('fetchLoadBoard', error as Error);
    setLoadBoardStore(
      produce((draft: LoadBoardState) => {
        draft.isError = true;
        draft.errorMessage = 'Error fetching loadboard';
        draft.isLoading = false;
      }),
    );
  }
};

export const appendNewSavedSearch = (search: SavedSearchT) => {
  setLoadBoardStore(
    produce((draft: LoadBoardState) => {
      draft.savedSearches = [...draft.savedSearches, search];

      if (
        search.category === SavedSearchCategory.Advanced ||
        search.category === SavedSearchCategory.Basic
      ) {
        draft.filters = [...draft.filters, search];
      }

      sessionStorage.setItem(
        'savedSearches',
        JSON.stringify(draft.savedSearches),
      );
    }),
  );
};

export const deleteSavedSearch = (search: SavedSearchT) => {
  setLoadBoardStore(
    produce((draft: LoadBoardState) => {
      const idx = draft.savedSearches.findIndex((s) => s.id === search.id);
      const idxFilters = draft.filters.findIndex((s) => s.id === search.id);

      if (idx > -1) {
        draft.savedSearches.splice(idx, 1);
        draft.savedSearches = [...draft.savedSearches];
      }

      if (idxFilters > -1) {
        draft.filters.splice(idxFilters, 1);
        draft.filters = [...draft.filters];
      }

      sessionStorage.setItem(
        'savedSearches',
        JSON.stringify(draft.savedSearches),
      );
    }),
  );
};

export const updateLoadboardItemField = (
  loadId: number,
  field: Part<ILoadBoardViewModel, keyof ILoadBoardViewModel>,
  value: unknown,
) => {
  const idx = loadBoardStore.items.findIndex((l) => l.loadId === loadId);

  if (idx >= 0) {
    // @ts-expect-error Ignore the issue with the unknown value.
    setLoadBoardStore('items', idx, field, value);
  }
};

export const removeLoadboardItem = (loadId: number) => {
  setLoadBoardStore('items', (items) =>
    items.filter((l) => l.loadId !== loadId),
  );
};

export const updateLoadboardItem = (load: ILoadBoardViewModel) => {
  const ind = loadBoardStore.items.findIndex(
    (item) => item.loadId === load.loadId,
  );
  if (ind >= 0) {
    setLoadBoardStore('items', ind, load);
  }
};

export const addLoadboardItem = (load: ILoadBoardViewModel) => {
  const idx = loadBoardStore.items.length;
  setLoadBoardStore('items', idx, load);
};

export const setFilteredItems = (items: ILoadBoardViewModel[]) => {
  setLoadBoardStore('filteredItems', items);
};

export { loadBoardStore, setLoadBoardStore };
